"use client";

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

const navItems = [
  { label: 'Centres', href: '/centres', hasDropdown: false },
  { label: 'Services', href: '/services', hasDropdown: false },
  { label: 'Prestataires', href: '/prestataires', hasDropdown: false },
  { label: 'Actualités', href: '/actualites', hasDropdown: false },
  { label: 'Recrutement', href: '/recrutement', hasDropdown: false },
  { label: 'Partenaires', href: '/partenaires', hasDropdown: false },
  { label: 'Contact', href: '/contact', hasDropdown: false },
];

// Langues affichées avant que Weglot ne soit prêt ; remplacées ensuite par
// les langues réellement activées côté Weglot (getAvailableLanguages).
const FALLBACK_LANGS = ['FR', 'NL'];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeLang, setActiveLang] = useState('FR');
  const [langOpen, setLangOpen] = useState(false);
  const [langs, setLangs] = useState<string[]>(FALLBACK_LANGS);
  const langRef = useRef<HTMLDivElement>(null);

  // Synchronise le sélecteur avec Weglot (langues dispo + langue courante).
  useEffect(() => {
    let cancelled = false;
    const applyCurrent = (l?: string) => setActiveLang(((l || 'fr') as string).toUpperCase());
    const sync = () => {
      const w = (window as any).Weglot;
      if (!w || !w.initialized) return false;
      try {
        const avail = w.getAvailableLanguages ? w.getAvailableLanguages() : null;
        if (Array.isArray(avail) && avail.length) setLangs(avail.map((x: string) => x.toUpperCase()));
        applyCurrent(w.getCurrentLang && w.getCurrentLang());
        if (w.on) w.on('languageChanged', (l: string) => applyCurrent(l));
      } catch { /* noop */ }
      return true;
    };
    if (sync()) return;
    const id = setInterval(() => { if (cancelled || sync()) clearInterval(id); }, 200);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Change la langue via Weglot.
  const switchLang = (lang: string) => {
    const w = (window as any).Weglot;
    if (w && w.switchTo) w.switchTo(lang.toLowerCase());
    setActiveLang(lang);
    setLangOpen(false);
    setMobileOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? 'header-scrolled' : ''}`}
      role="banner"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link href="/home-page" className="flex items-center flex-shrink-0" aria-label="Centres médicaux César De Paepe - Accueil">
            <img
              src="/assets/images/logo-cmmc-cdp.png"
              alt="Centres médicaux Medische centra César De Paepe"
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Navigation principale">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-primary hover:bg-primary-soft transition-all duration-200"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {item.label}
                {item.hasDropdown && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                )}
              </a>
            ))}
          </nav>

          {/* Right: Lang + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium text-text-muted hover:text-primary hover:bg-primary-soft transition-all duration-200 border border-border"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label="Changer de langue"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                {activeLang}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-card shadow-card overflow-hidden" role="listbox" aria-label="Sélectionner une langue">
                  {langs.map((lang) => (
                    <button
                      key={lang}
                      role="option"
                      aria-selected={activeLang === lang}
                      onClick={() => switchLang(lang)}
                      className={`block w-full text-left px-4 py-2 text-sm font-medium transition-colors ${activeLang === lang ? 'bg-primary-soft text-primary' : 'text-text-muted hover:bg-bg-gray hover:text-text-main'}`}
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a href="/contact" className="btn-primary text-sm py-2.5 px-5 whitespace-nowrap">
              Prendre rendez-vous
            </a>
          </div>

          {/* Mobile burger */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-text-muted hover:text-primary hover:bg-primary-soft transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12h18M3 6h18M3 18h18"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-nav"
        className={`lg:hidden bg-white border-t border-border transition-all duration-300 overflow-hidden ${mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
        aria-hidden={!mobileOpen}
      >
        <nav className="px-4 py-4 space-y-1" role="navigation" aria-label="Navigation mobile">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-text-muted hover:text-primary hover:bg-primary-soft transition-all"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {item.label}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </a>
          ))}
          <div className="pt-3 border-t border-border flex gap-2">
            {langs.map((lang) => (
              <button
                key={lang}
                onClick={() => switchLang(lang)}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${activeLang === lang ? 'bg-primary text-white' : 'bg-bg-gray text-text-muted hover:text-primary'}`}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {lang}
              </button>
            ))}
          </div>
          <a href="/contact" className="btn-primary w-full justify-center mt-2 text-sm">
            Prendre rendez-vous
          </a>
        </nav>
      </div>
    </header>
  );
}